import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Layers, Plus, Edit2, Trash2, Users, Save, CheckCircle, RefreshCw, MapPin, Shuffle, Wand2
} from "lucide-react";
import AdminSidebar from "../components/AdminSidebar";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QuestionSets() {
  const navigate = useNavigate();
  const [questionSets, setQuestionSets] = useState([]);
  const [questions, setQuestions] = useState({ section1: { questions: [] }, section2: { questions: [] }, section3: { questions: [] }});
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, set: null });
  const [editingSet, setEditingSet] = useState(null);
  const [selectedSetForAssign, setSelectedSetForAssign] = useState(null);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  
  const [newSet, setNewSet] = useState({
    name: "",
    description: "",
    section1_question_ids: [],
    section2_question_ids: [],
    section3_question_ids: []
  });

  const [showAutoAssignDialog, setShowAutoAssignDialog] = useState(false);
  const [autoAssignMode, setAutoAssignMode] = useState("block"); // "block" or "round-robin"

  // Get unique blocks from candidates
  const uniqueBlocks = useMemo(() => {
    const blocks = [...new Set(candidates.map(c => c.block).filter(Boolean))];
    return blocks.sort();
  }, [candidates]);

  // Get block-wise candidate distribution
  const blockDistribution = useMemo(() => {
    const dist = {};
    candidates.forEach(c => {
      const block = c.block || "Unknown";
      if (!dist[block]) {
        dist[block] = { 
          total: 0, 
          assigned: 0, 
          unassigned: 0,
          assignedSet: null,
          candidates: []
        };
      }
      dist[block].total++;
      dist[block].candidates.push(c);
      if (c.assigned_question_set) {
        dist[block].assigned++;
        // Track which set is assigned to this block
        const set = questionSets.find(s => s.id === c.assigned_question_set);
        if (set) dist[block].assignedSet = set.name;
      } else {
        dist[block].unassigned++;
      }
    });
    return dist;
  }, [candidates, questionSets]);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [setsRes, questionsRes, candidatesRes] = await Promise.all([
        axios.get(`${API}/admin/question-sets`),
        axios.get(`${API}/admin/questions`),
        axios.get(`${API}/admin/candidates/all`)  // Use legacy endpoint for full list
      ]);
      setQuestionSets(setsRes.data);
      setQuestions(questionsRes.data);
      // Handle both paginated and legacy response formats
      const candidatesData = candidatesRes.data.candidates || candidatesRes.data;
      setCandidates(Array.isArray(candidatesData) ? candidatesData : []);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSet = async () => {
    if (!newSet.name.trim()) {
      toast.error("Please enter a name for the question set");
      return;
    }
    try {
      if (editingSet) {
        await axios.put(`${API}/admin/question-sets/${editingSet.id}`, newSet);
        toast.success("Question set updated");
      } else {
        await axios.post(`${API}/admin/question-sets`, newSet);
        toast.success("Question set created");
      }
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save question set");
    }
  };

  const handleDeleteSet = async () => {
    if (!deleteDialog.set) return;
    try {
      await axios.delete(`${API}/admin/question-sets/${deleteDialog.set.id}`);
      toast.success("Question set deleted");
      setDeleteDialog({ open: false, set: null });
      fetchData();
    } catch (error) {
      toast.error("Failed to delete question set");
    }
  };

  const handleAssignSet = async () => {
    if (!selectedSetForAssign || selectedCandidateIds.length === 0) {
      toast.error("Please select a question set and candidates");
      return;
    }
    try {
      await axios.post(`${API}/admin/question-sets/assign`, {
        candidate_ids: selectedCandidateIds,
        question_set_id: selectedSetForAssign.id
      });
      toast.success(`Assigned to ${selectedCandidateIds.length} candidates`);
      setShowAssignDialog(false);
      setSelectedCandidateIds([]);
      fetchData();
    } catch (error) {
      toast.error("Failed to assign question set");
    }
  };

  const resetForm = () => {
    setNewSet({
      name: "",
      description: "",
      section1_question_ids: [],
      section2_question_ids: [],
      section3_question_ids: []
    });
    setEditingSet(null);
  };

  const openEditDialog = (set) => {
    setEditingSet(set);
    setNewSet({
      name: set.name,
      description: set.description || "",
      section1_question_ids: set.section1_question_ids || [],
      section2_question_ids: set.section2_question_ids || [],
      section3_question_ids: set.section3_question_ids || []
    });
    setShowCreateDialog(true);
  };

  const toggleQuestionSelection = (section, questionId) => {
    const key = `${section}_question_ids`;
    setNewSet(prev => ({
      ...prev,
      [key]: prev[key].includes(questionId)
        ? prev[key].filter(id => id !== questionId)
        : [...prev[key], questionId]
    }));
  };

  const getCandidatesWithSet = (setId) => {
    return candidates.filter(c => c.assigned_question_set === setId);
  };

  // Auto-assign question sets to blocks
  const handleAutoAssignByBlock = async () => {
    if (questionSets.length === 0) {
      toast.error("Please create question sets first");
      return;
    }

    const blocks = Object.keys(blockDistribution).sort();
    const unassignedCandidates = candidates.filter(c => !c.has_submitted && !c.assigned_question_set);
    
    if (unassignedCandidates.length === 0) {
      toast.error("No unassigned candidates found");
      return;
    }

    try {
      let assignmentCount = 0;

      if (autoAssignMode === "block") {
        // Assign one set per block (rotate through available sets)
        for (let i = 0; i < blocks.length; i++) {
          const block = blocks[i];
          const setIndex = i % questionSets.length;
          const set = questionSets[setIndex];
          
          const blockCandidates = unassignedCandidates.filter(c => c.block === block);
          if (blockCandidates.length > 0) {
            await axios.post(`${API}/admin/question-sets/assign`, {
              candidate_ids: blockCandidates.map(c => c.id),
              question_set_id: set.id
            });
            assignmentCount += blockCandidates.length;
          }
        }
      } else {
        // Round-robin: distribute evenly across all candidates
        for (let i = 0; i < unassignedCandidates.length; i++) {
          const setIndex = i % questionSets.length;
          const set = questionSets[setIndex];
          
          await axios.post(`${API}/admin/question-sets/assign`, {
            candidate_ids: [unassignedCandidates[i].id],
            question_set_id: set.id
          });
          assignmentCount++;
        }
      }

      toast.success(`Auto-assigned ${assignmentCount} candidates across ${questionSets.length} sets`);
      setShowAutoAssignDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to auto-assign");
    }
  };

  // Assign a specific set to a specific block
  const handleAssignSetToBlock = async (setId, block) => {
    const blockCandidates = candidates.filter(c => c.block === block && !c.has_submitted);
    if (blockCandidates.length === 0) {
      toast.error("No available candidates in this block");
      return;
    }

    try {
      await axios.post(`${API}/admin/question-sets/assign`, {
        candidate_ids: blockCandidates.map(c => c.id),
        question_set_id: setId
      });
      toast.success(`Assigned to ${blockCandidates.length} candidates in ${block}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to assign");
    }
  };

  return (
    <AdminSidebar>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Question Sets</h1>
            <p className="text-sm text-gray-500">Create and assign question sets to candidates by location</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAutoAssignDialog(true)}
              disabled={questionSets.length === 0}
              className="bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
              data-testid="auto-assign-btn"
            >
              <Wand2 className="w-4 h-4 mr-2" /> Auto-Assign
            </Button>
            <Button
              size="sm"
              onClick={() => { resetForm(); setShowCreateDialog(true); }}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="create-set-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Create Set
            </Button>
          </div>
        </div>

        {/* Block Distribution Summary */}
        {Object.keys(blockDistribution).length > 0 && questionSets.length > 0 && (
          <Card className="mb-6 border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-600" />
                Location-wise Question Set Distribution
              </CardTitle>
              <CardDescription>
                Assign different question sets to different blocks/locations to prevent cheating
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(blockDistribution).map(([block, data]) => (
                  <div key={block} className="p-3 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{block}</span>
                      <Badge variant="outline" className="text-xs">
                        {data.total} candidates
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      {data.assignedSet ? (
                        <Badge className="bg-purple-100 text-purple-700 text-xs">
                          {data.assignedSet}
                        </Badge>
                      ) : (
                        <Select onValueChange={(setId) => handleAssignSetToBlock(setId, block)}>
                          <SelectTrigger className="h-7 text-xs w-32">
                            <SelectValue placeholder="Assign Set" />
                          </SelectTrigger>
                          <SelectContent>
                            {questionSets.map(set => (
                              <SelectItem key={set.id} value={set.id}>{set.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <span className="text-xs text-gray-500">
                        {data.assigned}/{data.total} assigned
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : questionSets.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Layers className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No Question Sets Yet</h3>
              <p className="text-gray-500 mb-4">Create 3-5 question sets to distribute among different locations</p>
              <Button onClick={() => setShowCreateDialog(true)} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-2" /> Create Your First Set
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {questionSets.map((set) => {
              const assignedCandidates = getCandidatesWithSet(set.id);
              return (
                <Card key={set.id} className="border-l-4 border-l-purple-500">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Layers className="w-5 h-5 text-purple-600" />
                          {set.name}
                        </CardTitle>
                        {set.description && (
                          <CardDescription className="mt-1">{set.description}</CardDescription>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setSelectedSetForAssign(set); setShowAssignDialog(true); }}
                          data-testid={`assign-set-${set.id}`}
                        >
                          <Users className="w-4 h-4 mr-2" /> Assign
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(set)}
                          data-testid={`edit-set-${set.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => setDeleteDialog({ open: true, set })}
                          data-testid={`delete-set-${set.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                          Sec I: {set.section1_question_ids?.length || 0} Qs
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-amber-50 text-amber-700">
                          Sec II: {set.section2_question_ids?.length || 0} Qs
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-teal-50 text-teal-700">
                          Sec III: {set.section3_question_ids?.length || 0} Qs
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-purple-50 text-purple-700">
                          <Users className="w-3 h-3 mr-1" /> {assignedCandidates.length} Candidates
                        </Badge>
                      </div>
                    </div>
                    {assignedCandidates.length > 0 && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-2">Assigned to:</p>
                        <div className="flex flex-wrap gap-1">
                          {assignedCandidates.slice(0, 5).map(c => (
                            <Badge key={c.id} variant="secondary" className="text-xs">
                              {c.name}
                            </Badge>
                          ))}
                          {assignedCandidates.length > 5 && (
                            <Badge variant="secondary" className="text-xs">
                              +{assignedCandidates.length - 5} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

      {/* Create/Edit Question Set Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowCreateDialog(open); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSet ? "Edit Question Set" : "Create Question Set"}</DialogTitle>
            <DialogDescription>
              Select which questions to include in this set
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="set-name">Set Name *</Label>
                <Input
                  id="set-name"
                  placeholder="e.g., Set A, Set B"
                  value={newSet.name}
                  onChange={(e) => setNewSet(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="set-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="set-description">Description</Label>
                <Input
                  id="set-description"
                  placeholder="Optional description"
                  value={newSet.description}
                  onChange={(e) => setNewSet(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
            </div>

            <Tabs defaultValue="section1" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="section1">
                  Section I ({newSet.section1_question_ids.length} selected)
                </TabsTrigger>
                <TabsTrigger value="section2">
                  Section II ({newSet.section2_question_ids.length} selected)
                </TabsTrigger>
                <TabsTrigger value="section3">
                  Section III ({newSet.section3_question_ids.length} selected)
                </TabsTrigger>
              </TabsList>

              <TabsContent value="section1">
                <ScrollArea className="h-[300px] border rounded-lg p-4">
                  <div className="space-y-2">
                    <div className="flex justify-between mb-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setNewSet(prev => ({
                          ...prev,
                          section1_question_ids: questions.section1?.questions?.map(q => q.id) || []
                        }))}
                      >
                        Select All
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setNewSet(prev => ({ ...prev, section1_question_ids: [] }))}
                      >
                        Clear All
                      </Button>
                    </div>
                    {questions.section1?.questions?.map((q) => (
                      <div key={q.id} className="flex items-start gap-3 p-2 border rounded hover:bg-gray-50">
                        <Checkbox
                          checked={newSet.section1_question_ids.includes(q.id)}
                          onCheckedChange={() => toggleQuestionSelection("section1", q.id)}
                        />
                        <div className="flex-1">
                          <span className="text-xs font-medium text-emerald-600">Q{q.id}</span>
                          <p className="text-sm text-gray-700">{q.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="section2">
                <ScrollArea className="h-[300px] border rounded-lg p-4">
                  <div className="space-y-2">
                    <div className="flex justify-between mb-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setNewSet(prev => ({
                          ...prev,
                          section2_question_ids: questions.section2?.questions?.map(q => q.id) || []
                        }))}
                      >
                        Select All
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setNewSet(prev => ({ ...prev, section2_question_ids: [] }))}
                      >
                        Clear All
                      </Button>
                    </div>
                    {questions.section2?.questions?.map((q) => (
                      <div key={q.id} className="flex items-start gap-3 p-2 border rounded hover:bg-gray-50">
                        <Checkbox
                          checked={newSet.section2_question_ids.includes(q.id)}
                          onCheckedChange={() => toggleQuestionSelection("section2", q.id)}
                        />
                        <div className="flex-1">
                          <span className="text-xs font-medium text-amber-600">Q{q.id}</span>
                          <p className="text-sm text-gray-700">{q.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="section3">
                <ScrollArea className="h-[300px] border rounded-lg p-4">
                  <div className="space-y-2">
                    <div className="flex justify-between mb-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setNewSet(prev => ({
                          ...prev,
                          section3_question_ids: questions.section3?.questions?.map(q => q.id) || []
                        }))}
                      >
                        Select All
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setNewSet(prev => ({ ...prev, section3_question_ids: [] }))}
                      >
                        Clear All
                      </Button>
                    </div>
                    {questions.section3?.questions?.map((q) => (
                      <div key={q.id} className="flex items-start gap-3 p-2 border rounded hover:bg-gray-50">
                        <Checkbox
                          checked={newSet.section3_question_ids.includes(q.id)}
                          onCheckedChange={() => toggleQuestionSelection("section3", q.id)}
                        />
                        <div className="flex-1">
                          <span className="text-xs font-medium text-teal-600">Q{q.id}</span>
                          <p className="text-sm text-gray-700">{q.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setShowCreateDialog(false); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateSet} className="bg-purple-600 hover:bg-purple-700" data-testid="save-set-btn">
              <Save className="w-4 h-4 mr-2" /> {editingSet ? "Update Set" : "Create Set"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Question Set Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={(open) => { if (!open) setSelectedCandidateIds([]); setShowAssignDialog(open); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign "{selectedSetForAssign?.name}" to Candidates</DialogTitle>
            <DialogDescription>
              Select candidates who will receive this question set
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex justify-between">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSelectedCandidateIds(candidates.filter(c => !c.has_submitted).map(c => c.id))}
              >
                Select All Available
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSelectedCandidateIds([])}
              >
                Clear Selection
              </Button>
            </div>
            
            <ScrollArea className="h-[400px] border rounded-lg p-4">
              <div className="space-y-2">
                {candidates.filter(c => !c.has_submitted).map((candidate) => (
                  <div key={candidate.id} className="flex items-center gap-3 p-2 border rounded hover:bg-gray-50">
                    <Checkbox
                      checked={selectedCandidateIds.includes(candidate.id)}
                      onCheckedChange={(checked) => {
                        setSelectedCandidateIds(prev => 
                          checked 
                            ? [...prev, candidate.id]
                            : prev.filter(id => id !== candidate.id)
                        );
                      }}
                    />
                    <div className="flex-1">
                      <p className="font-medium">{candidate.name}</p>
                      <p className="text-xs text-gray-500">{candidate.registration_number} • {candidate.block}</p>
                    </div>
                    {candidate.assigned_question_set && (
                      <Badge variant="secondary" className="text-xs">
                        Has Set
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelectedCandidateIds([]); setShowAssignDialog(false); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignSet} 
              disabled={selectedCandidateIds.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-assign-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" /> Assign to {selectedCandidateIds.length} Candidates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Question Set?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDialog.set?.name}"? 
              This will also remove the assignment from all candidates.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteSet}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Auto-Assign Dialog */}
      <Dialog open={showAutoAssignDialog} onOpenChange={setShowAutoAssignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-amber-600" />
              Auto-Assign Question Sets
            </DialogTitle>
            <DialogDescription>
              Automatically distribute question sets across candidates to prevent cheating during exams
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Available Sets:</strong> {questionSets.length} question sets<br />
                <strong>Unassigned Candidates:</strong> {candidates.filter(c => !c.has_submitted && !c.assigned_question_set).length}<br />
                <strong>Unique Locations:</strong> {uniqueBlocks.length} blocks
              </p>
            </div>

            <div className="space-y-3">
              <Label>Assignment Mode</Label>
              <div className="grid grid-cols-2 gap-3">
                <div 
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${autoAssignMode === 'block' ? 'border-purple-500 bg-purple-50' : 'hover:bg-gray-50'}`}
                  onClick={() => setAutoAssignMode('block')}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-purple-600" />
                    <span className="font-medium text-sm">By Location</span>
                  </div>
                  <p className="text-xs text-gray-600">
                    Same set for all candidates in a block. Different blocks get different sets.
                  </p>
                </div>
                <div 
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${autoAssignMode === 'round-robin' ? 'border-purple-500 bg-purple-50' : 'hover:bg-gray-50'}`}
                  onClick={() => setAutoAssignMode('round-robin')}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Shuffle className="w-4 h-4 text-purple-600" />
                    <span className="font-medium text-sm">Round Robin</span>
                  </div>
                  <p className="text-xs text-gray-600">
                    Evenly distribute sets across all candidates regardless of location.
                  </p>
                </div>
              </div>
            </div>

            {autoAssignMode === 'block' && (
              <div className="p-3 bg-amber-50 rounded-lg text-xs text-amber-800">
                <strong>Preview:</strong> Sets will be assigned in rotation:
                <ul className="mt-1 ml-4 list-disc">
                  {uniqueBlocks.slice(0, 5).map((block, i) => (
                    <li key={block}>
                      {block} → {questionSets[i % questionSets.length]?.name || 'Set'}
                    </li>
                  ))}
                  {uniqueBlocks.length > 5 && <li>...and {uniqueBlocks.length - 5} more blocks</li>}
                </ul>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAutoAssignDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAutoAssignByBlock}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-auto-assign-btn"
            >
              <Wand2 className="w-4 h-4 mr-2" /> Auto-Assign Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </AdminSidebar>
  );
}
